/**
 * Emoji suggestion system for the TipTap editor.
 *
 * Triggered by typing `:` — displays a compact 8-column grid of curated
 * work-oriented emojis (no query), or a filtered list (with query).
 * Self-contained: no dependency on other RichTextField modules.
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { largeurUtile } from '../../shell/cornerDock';

const EMOJI_WORK_SECTIONS = [
    { label: 'Réactions', names: [
        'thumbs_up', 'thumbs_down', 'clapping_hands', 'raising_hands',
        'folded_hands', 'fire', 'red_heart', 'star', 'sparkles',
        'party_popper', 'trophy', 'rocket', 'ok_hand', 'victory_hand',
        'flexed_biceps', 'eyes', 'hundred_points', 'light_bulb',
    ]},
    { label: 'Travail', names: [
        'check_mark_button', 'cross_mark', 'warning', 'exclamation_mark',
        'question_mark', 'stop_sign', 'construction', 'hourglass_done',
        'alarm_clock', 'calendar', 'pushpin', 'round_pushpin',
        'clipboard', 'memo', 'link', 'magnifying_glass_tilted_left',
        'bar_chart', 'chart_increasing', 'gear', 'wrench',
        'locked', 'unlocked', 'key', 'package',
    ]},
    { label: 'Personnes', names: [
        'grinning_face', 'slightly_smiling_face', 'winking_face',
        'smiling_face_with_heart-eyes', 'face_with_tears_of_joy',
        'thinking_face', 'face_with_monocle', 'nerd_face',
        'smiling_face_with_sunglasses', 'waving_hand',
        'person_raising_hand', 'person_tipping_hand',
        'person_shrugging', 'handshake',
    ]},
    { label: 'Nature & Drapeaux', names: [
        'sun', 'cloud', 'rainbow', 'four_leaf_clover',
        'seedling', 'deciduous_tree', 'globe_showing_europe-africa',
        'chequered_flag', 'triangular_flag',
    ]},
];

/**
 * Builds the default emoji grid (sections with curated picks) from the full emoji catalog.
 * @param {Array<{name: string, emoji: string}>} all - Full emoji list from @tiptap/extension-emoji storage.
 * @returns {Array<{label: string, emojis: Array}>} Sections for the grid display.
 */
export function buildEmojiDefaults(all) {
    if (!all.length) return [];
    const byName = new Map(all.map((e) => [e.name, e]));
    const sections = [];
    for (const sec of EMOJI_WORK_SECTIONS) {
        const emojis = sec.names.map((n) => byName.get(n)).filter(Boolean);
        if (emojis.length) sections.push({ label: sec.label, emojis });
    }
    return sections;
}

const EmojiSuggestionMenu = forwardRef(function EmojiSuggestionMenu({ items, command }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const menuRef = useRef(null);
    const selectedRef = useRef(0);

    const isGridMode = Array.isArray(items[0]?.emojis);
    const flatItems = useMemo(() => {
        if (!isGridMode) return items;
        return items.flatMap((sec) => sec.emojis);
    }, [items, isGridMode]);

    useEffect(() => {
        setSelectedIndex(0);
        selectedRef.current = 0;
    }, [items]);

    useEffect(() => {
        if (!menuRef.current) return;
        const el = menuRef.current.querySelector('[data-emoji-selected="true"]');
        el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
        onKeyDown({ event }) {
            const len = flatItems.length;
            if (!len) return false;
            if (event.key === 'ArrowDown') {
                const step = isGridMode ? 8 : 1; // 8 = grid-cols-8
                const next = Math.min(selectedRef.current + step, len - 1);
                setSelectedIndex(next);
                selectedRef.current = next;
                return true;
            }
            if (event.key === 'ArrowUp') {
                const step = isGridMode ? 8 : 1; // 8 = grid-cols-8
                const next = Math.max(selectedRef.current - step, 0);
                setSelectedIndex(next);
                selectedRef.current = next;
                return true;
            }
            if (event.key === 'ArrowRight' && isGridMode) {
                const next = Math.min(selectedRef.current + 1, len - 1);
                setSelectedIndex(next);
                selectedRef.current = next;
                return true;
            }
            if (event.key === 'ArrowLeft' && isGridMode) {
                const next = Math.max(selectedRef.current - 1, 0);
                setSelectedIndex(next);
                selectedRef.current = next;
                return true;
            }
            if (event.key === 'Enter') {
                const item = flatItems[selectedRef.current];
                if (item) command(item);
                return true;
            }
            return false;
        },
    }));

    if (!flatItems.length) {
        return (
            <div className="bg-white rounded-xl shadow-xl border border-stone-200 p-3 text-stone-400 text-sm text-center min-w-[200px]">
                Aucun emoji trouvé
            </div>
        );
    }

    if (isGridMode) {
        let globalIdx = 0;
        return (
            <div ref={menuRef} className="bg-white rounded-xl shadow-xl border border-stone-200 p-2 max-h-80 w-[296px] overflow-y-auto">
                {items.map((section) => (
                    <div key={section.label} className="mb-1.5">
                        <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider px-1 pb-1">{section.label}</p>
                        <div className="grid grid-cols-8 gap-0.5">
                            {section.emojis.map((item) => {
                                const idx = globalIdx++;
                                const isSel = idx === selectedIndex;
                                return (
                                    <button
                                        key={item.name}
                                        type="button"
                                        data-emoji-selected={isSel || undefined}
                                        onClick={() => command(item)}
                                        title={`:${item.name}:`}
                                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xl transition-colors cursor-pointer ${
                                            isSel ? 'bg-blue-100 ring-1 ring-blue-400' : 'hover:bg-stone-100'
                                        }`}
                                    >
                                        {item.fallbackImage
                                            ? <img src={item.fallbackImage} alt={item.name} className="w-5 h-5" />
                                            : item.emoji}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div ref={menuRef} className="bg-white rounded-xl shadow-xl border border-stone-200 p-1 max-h-72 w-72 overflow-y-auto">
            {items.map((item, index) => (
                <button
                    key={item.name}
                    type="button"
                    data-emoji-selected={index === selectedIndex || undefined}
                    onClick={() => command(item)}
                    className={`flex items-center gap-2.5 w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors cursor-pointer ${
                        index === selectedIndex ? 'bg-blue-50 text-stone-900' : 'text-stone-700 hover:bg-stone-50'
                    }`}
                >
                    <span className="text-xl w-7 text-center shrink-0">
                        {item.fallbackImage
                            ? <img src={item.fallbackImage} alt={item.name} className="w-5 h-5 inline-block" />
                            : item.emoji}
                    </span>
                    <span className="truncate text-stone-500">:{item.name}:</span>
                </button>
            ))}
        </div>
    );
});

/**
 * Creates the TipTap suggestion renderer for the emoji picker.
 * Same portal pattern as createSlashSuggestionRenderer().
 */
export function createEmojiSuggestionRenderer() {
    let root = null;
    let popup = null;
    let componentRef = { current: null };

    return {
        onStart(props) {
            popup = document.createElement('div');
            popup.style.position = 'fixed';
            popup.style.zIndex = '9999';
            popup.style.visibility = 'hidden';
            document.body.appendChild(popup);
            updatePopup(props);
        },

        onUpdate(props) {
            updatePopup(props);
        },

        onKeyDown(props) {
            if (props.event.key === 'Escape') {
                destroyPopup();
                return true;
            }
            return componentRef.current?.onKeyDown(props) ?? false;
        },

        onExit() {
            destroyPopup();
        },
    };

    function updatePopup(props) {
        if (!popup) return;

        const rect = props.clientRect?.();
        if (rect) {
            const menuHeight = 340;
            const menuWidth = 300;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow < menuHeight && rect.top > menuHeight
                ? rect.top - menuHeight - 4
                : rect.bottom + 4;
            const left = Math.min(rect.left, largeurUtile() - menuWidth - 8);
            popup.style.left = `${Math.max(8, left)}px`;
            popup.style.top = `${top}px`;
            popup.style.visibility = 'visible';
        }

        if (!root) {
            root = createRoot(popup);
        }

        root.render(
            <EmojiSuggestionMenu
                ref={componentRef}
                items={props.items}
                command={(item) => props.command({ name: item.name })}
            />
        );
    }

    function destroyPopup() {
        if (root) {
            root.unmount();
            root = null;
        }
        if (popup) {
            popup.remove();
            popup = null;
        }
    }
}
